import React, { useRef, useState } from 'react';
import { AlertTriangle, ClipboardPaste, UploadCloud } from 'lucide-react';
import { readExcel } from '../../utils/excelfunction';
import { isFileFormatSupported } from '../../utils/importElevesUtils';
import { parserLignesImport, parserPressePapier } from '../../utils/importEtablissement';
import TemplateImportEleve from '../VueEleve/TemplateImportEleve';

/**
 * Étape 1 du wizard : lecture du fichier (ou du presse-papier).
 * Deux formats exclusifs : Nom, Prénom, Classe (le modèle) ou bien Élèves,
 * Classe (export Pronote, « NOM Prénom » découpé par la casse) ; toute autre
 * colonne d'un export Pronote/SIECLE est ignorée. Les valeurs partent BRUTES
 * au back, qui normalise.
 */
function EtapeFichier({ onDonnees }) {
    const [dragOver, setDragOver] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [erreur, setErreur] = useState('');
    const [analyse, setAnalyse] = useState(false);
    const fileInputRef = useRef(null);

    const livrer = (resultat, nomSource) => {
        if (resultat.erreur) {
            setErreur(resultat.erreur);
            setSelectedFile(null);
            return;
        }
        setErreur('');
        setSelectedFile({ name: nomSource });
        onDonnees(resultat.lignes, resultat.sansClasse || 0);
    };

    const handleFiles = async (file) => {
        if (!file || !(file instanceof Blob)) {
            setErreur('Aucun fichier valide fourni.');
            return;
        }
        if (!isFileFormatSupported(file.name)) {
            setErreur(`Format non supporté. Utilisez CSV, XLSX, XLS ou ODS. Fichier détecté : ${file.name}`);
            return;
        }
        setAnalyse(true);
        setErreur('');
        try {
            const sheetsData = await readExcel(file);
            livrer(parserLignesImport(sheetsData), file.name);
        } catch (e) {
            console.error('Erreur lecture fichier import établissement :', e);
            setErreur('Erreur lors de la lecture du fichier.');
        }
        setAnalyse(false);
    };

    const handlePastePressePapier = async () => {
        try {
            const texte = await navigator.clipboard.readText();
            livrer(parserPressePapier(texte), 'Presse-papier');
        } catch (e) {
            console.error('Erreur lecture presse-papier :', e);
            setErreur("Erreur lors de la lecture du presse-papier. Copiez vos données depuis Excel puis réessayez.");
        }
    };

    return (
        <div>
            {/* Un seul encart : le quoi-faire en 3 lignes courtes */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 space-y-1.5">
                <p className="text-sm text-blue-900">
                    📄 <span className="font-semibold">Envoyez l'export des fiches élèves</span> de
                    tout l'établissement (colonnes : Nom, Prénom, Classe, ou bien Élèves, Classe pour un export Pronote).
                    Les fiches seront synchronisées avec le GAR à la première connexion des élèves :
                    le professeur de la classe pourra alors leur envoyer des quiz en ligne.
                </p>
                <p className="text-sm text-blue-900">
                    👋 Un élève absent du fichier = un élève qui a <span className="font-semibold">quitté l'établissement</span>.
                </p>
            </div>

            {/* Zone de drag & drop */}
            <div
                className={`relative group border-2 border-dashed rounded-2xl p-8 mb-5 transition-all duration-300 cursor-pointer ${
                    dragOver
                        ? 'border-blue-500 bg-blue-50 scale-[1.01]'
                        : selectedFile && !erreur
                            ? 'border-emerald-400 bg-emerald-50'
                            : 'border-slate-300 hover:border-blue-400 hover:bg-slate-50'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files[0]);
                }}
                onClick={() => fileInputRef.current?.click()}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => { if (e.target.files.length > 0) handleFiles(e.target.files[0]); }}
                    accept=".csv,.xlsx,.xls,.ods"
                    className="hidden"
                />
                <div className="relative text-center">
                    {analyse ? (
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-3" />
                            <p className="text-sm text-gray-600 font-medium">Lecture du fichier...</p>
                        </div>
                    ) : (
                        <>
                            <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center transition-all duration-300 ${
                                dragOver
                                    ? 'bg-blue-600 scale-110'
                                    : selectedFile && !erreur
                                        ? 'bg-emerald-500'
                                        : 'bg-slate-100 group-hover:bg-blue-600'
                            }`}>
                                <UploadCloud className={`w-8 h-8 ${
                                    dragOver || (selectedFile && !erreur) ? 'text-white' : 'text-slate-400 group-hover:text-white'
                                }`} />
                            </div>
                            {selectedFile && !erreur ? (
                                <div>
                                    <p className="text-lg font-semibold text-emerald-700 mb-1">Fichier lu !</p>
                                    <p className="text-emerald-600 text-sm bg-emerald-100 px-4 py-2 rounded-lg inline-block break-all">
                                        📄 {selectedFile.name}
                                    </p>
                                </div>
                            ) : (
                                <div>
                                    <p className={`text-lg font-semibold mb-1 ${dragOver ? 'text-blue-600' : 'text-slate-700'}`}>
                                        {dragOver ? 'Déposez votre fichier ici !' : 'Glissez-déposez votre fichier ici'}
                                    </p>
                                    <p className="text-slate-500 text-sm">ou cliquez pour parcourir (CSV, XLSX, XLS, ODS)</p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Séparateur */}
            <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
                <span className="text-slate-400 text-xs font-medium">ou</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
            </div>

            {/* Collage presse-papier */}
            <button
                onClick={handlePastePressePapier}
                className="group w-full p-4 rounded-2xl border-2 border-violet-200 hover:border-violet-300 hover:bg-violet-50/50 transition-all duration-300 text-left"
            >
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-400 to-indigo-500 group-hover:scale-105 transition-transform flex-shrink-0">
                        <ClipboardPaste className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm sm:text-base">
                            Coller depuis le presse-papier
                            <span className="ml-2 px-1.5 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-mono rounded">Ctrl+V</span>
                        </p>
                        <p className="text-xs text-slate-500">
                            Copiez le tableau avec ses en-têtes (Nom, Prénom, Classe, ou bien Élèves, Classe) depuis Excel ou Pronote et collez-le ici
                        </p>
                    </div>
                </div>
            </button>

            {/* Gabarit téléchargeable */}
            <TemplateImportEleve />

            {/* Erreur de lecture / colonnes manquantes */}
            {erreur ? (
                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 mt-4 flex items-start gap-3">
                    <div className="p-1.5 bg-red-500 rounded-lg flex-shrink-0">
                        <AlertTriangle className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-red-800 font-semibold mb-0.5 text-sm">Fichier refusé</p>
                        <p className="text-red-700 text-xs sm:text-sm break-words">{erreur}</p>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export default EtapeFichier;
